import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import classNames from 'classnames';
import styles from './index.module.scss';

// ─── Types ───────────────────────────────────────────────────────────────────

type ToggleGroupType = 'single' | 'multiple';
type ToggleGroupVariant = 'default' | 'outline';
type ToggleGroupSize = 'default' | 'sm' | 'lg';

interface ToggleGroupContextValue {
  type: ToggleGroupType;
  value: string[];
  onValueChange: (value: string) => void;
  variant: ToggleGroupVariant;
  size: ToggleGroupSize;
  disabled: boolean;
}

interface ToggleGroupSingleProps {
  type: 'single';
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
}

interface ToggleGroupMultipleProps {
  type: 'multiple';
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
}

type ToggleGroupBaseProps = {
  children: React.ReactNode;
  className?: string;
  variant?: ToggleGroupVariant;
  size?: ToggleGroupSize;
  disabled?: boolean;
  orientation?: 'horizontal' | 'vertical';
};

type ToggleGroupProps = ToggleGroupBaseProps & (ToggleGroupSingleProps | ToggleGroupMultipleProps);

interface ToggleGroupItemProps {
  children: React.ReactNode;
  value: string;
  className?: string;
  disabled?: boolean;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const ToggleGroupContext = createContext<ToggleGroupContextValue>({
  type: 'single',
  value: [],
  onValueChange: () => {},
  variant: 'default',
  size: 'default',
  disabled: false,
});

// ─── ToggleGroup ─────────────────────────────────────────────────────────────

export function ToggleGroup(props: ToggleGroupProps) {
  const {
    children,
    className,
    variant = 'default',
    size = 'default',
    disabled = false,
    orientation = 'horizontal',
    type,
  } = props;

  const [internalValue, setInternalValue] = React.useState<string[]>(() => {
    if (type === 'single') {
      const sp = props as ToggleGroupSingleProps;
      return sp.defaultValue ? [sp.defaultValue] : [];
    }
    const mp = props as ToggleGroupMultipleProps;
    return mp.defaultValue || [];
  });

  const isControlled = props.value !== undefined;

  const currentValue = useMemo(() => {
    if (!isControlled) return internalValue;
    if (type === 'single') {
      const v = (props as ToggleGroupSingleProps).value;
      return v ? [v] : [];
    }
    return (props as ToggleGroupMultipleProps).value || [];
  }, [isControlled, internalValue, type, props]);

  const handleValueChange = useCallback((itemValue: string) => {
    if (type === 'single') {
      const sp = props as ToggleGroupSingleProps;
      const newValue = currentValue.includes(itemValue) ? '' : itemValue;
      if (!isControlled) {
        setInternalValue(newValue ? [newValue] : []);
      }
      sp.onValueChange?.(newValue);
    } else {
      const mp = props as ToggleGroupMultipleProps;
      const newValue = currentValue.includes(itemValue)
        ? currentValue.filter((v) => v !== itemValue)
        : [...currentValue, itemValue];
      if (!isControlled) {
        setInternalValue(newValue);
      }
      mp.onValueChange?.(newValue);
    }
  }, [type, currentValue, isControlled, props]);

  const ctx = useMemo<ToggleGroupContextValue>(() => ({
    type,
    value: currentValue,
    onValueChange: handleValueChange,
    variant,
    size,
    disabled,
  }), [type, currentValue, handleValueChange, variant, size, disabled]);

  return (
    <ToggleGroupContext.Provider value={ctx}>
      <div
        className={classNames(
          styles['toggle-group'],
          styles[`toggle-group--${orientation}`],
          styles[`toggle-group--${variant}`],
          className,
        )}
        role="group"
        data-orientation={orientation}
      >
        {children}
      </div>
    </ToggleGroupContext.Provider>
  );
}

// ─── ToggleGroupItem ─────────────────────────────────────────────────────────

export function ToggleGroupItem({
  children,
  value,
  className,
  disabled: itemDisabled,
}: ToggleGroupItemProps) {
  const {
    value: groupValue,
    onValueChange,
    variant,
    size,
    disabled: groupDisabled,
  } = useContext(ToggleGroupContext);

  const disabled = itemDisabled || groupDisabled;
  const isActive = groupValue.includes(value);

  const handleClick = useCallback(() => {
    if (disabled) return;
    onValueChange(value);
  }, [disabled, onValueChange, value]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isActive}
      aria-disabled={disabled}
      data-state={isActive ? 'on' : 'off'}
      disabled={disabled}
      className={classNames(
        styles['toggle-group-item'],
        styles[`toggle-group-item--${variant}`],
        styles[`toggle-group-item--${size}`],
        isActive && styles['toggle-group-item--active'],
        disabled && styles['toggle-group-item--disabled'],
        className,
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {children}
    </button>
  );
}

export default ToggleGroup;
